/**
 * coverage-lag — late-coverage label helper (A9a), typed re-export.
 *
 * The pure date logic lives in `./coverage-lag.core.js` (plain JS so the acceptance
 * test `backend/scripts/test-a9a-late-label.js` can require it directly under node).
 * This wrapper adds the TypeScript surface the React components consume and a small
 * convenience selector for picking the right coverage-time field off a breaking_news
 * doc.
 *
 * Why a label at all: Wave-1 freshness lets older-but-newsworthy tweets be covered
 * late, and `publishedAt` is BACKDATED to the source tweet's post time by design. When
 * coverage materially lags the source tweet (> 24h), we say so honestly — "Posted
 * Jun 6 — covered Jun 9" — instead of looking like we backdated silently. Within 24h
 * the helper returns null and the UI renders nothing (zero-change fresh path).
 */
const core = require('./coverage-lag.core.js');

/** Any date-ish value the helper accepts (Firestore Timestamp | Date | ISO string | epoch). */
export type DateInput = unknown;

export interface CoverageLag {
  /** Short label for the source-tweet date, e.g. "Jun 6" (or "Jun 6, 2025" across years). */
  postedLabel: string;
  /** Short label for the coverage date, e.g. "Jun 9". */
  coveredLabel: string;
}

/**
 * Coverage lagging the source tweet by MORE than this (strictly) triggers the label.
 * Named display-only constant — not operator-tunable config. 24h.
 */
export const LATE_COVERAGE_THRESHOLD_MS: number = core.LATE_COVERAGE_THRESHOLD_MS;

/**
 * Given the source-tweet time and the wall-clock coverage time, returns the
 * late-coverage labels, or null when coverage was not materially late (<= 24h),
 * when either input is missing/unparseable, or when coverage predates the tweet.
 * Never throws.
 */
export function computeCoverageLag(
  sourceTweetTime: DateInput,
  coverageTime: DateInput,
): CoverageLag | null {
  return core.computeCoverageLag(sourceTweetTime, coverageTime);
}

/** Minimal shape needed to resolve a breaking_news doc's wall-clock coverage time. */
interface CoverageTimeFields {
  /** Wall-clock auto-publish moment (set only on auto-published docs). */
  autoPublishedAt?: DateInput;
  /** Wall-clock creation moment (always present). */
  createdAt?: DateInput;
}

/**
 * Pick the wall-clock "coverage time" for a breaking_news doc.
 *
 * `autoPublishedAt` is the true wall-clock moment we published an auto-drafted story
 * (storeDraft stamps it only on auto-publish; `publishedAt` is backdated to the tweet
 * time so it CANNOT serve here). When absent (human-published / draft), `createdAt` —
 * always a wall-clock server timestamp — is the best available proxy for when we
 * actually covered the story.
 */
export function coverageTimeOf(doc: CoverageTimeFields): DateInput {
  return doc.autoPublishedAt ?? doc.createdAt ?? null;
}

/**
 * Convenience: compute the late-coverage label straight from a breaking_news-shaped
 * doc carrying `sourceTweetDate` + (`autoPublishedAt` | `createdAt`). Returns null when
 * there is no reliable source date (e.g. human-written posts, or docs predating the
 * field) — silent, not broken.
 */
export function coverageLagForDoc(
  doc: { sourceTweetDate?: DateInput } & CoverageTimeFields,
): CoverageLag | null {
  return computeCoverageLag(doc.sourceTweetDate ?? null, coverageTimeOf(doc));
}
